import React, { ReactElement, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { green, orange } from '@material-ui/core/colors';

import Button from '@material-ui/core/Button';
import { useForm } from 'react-hook-form';
import TextField from '@material-ui/core/TextField';

import {
  DataGrid,
  GridCellParams,
  GridCellValue,
  GridColDef,
  GridPageChangeParams,
  GridSortModel,
  GridSortModelParams,
  ValueGetterParams,
} from '@material-ui/data-grid';

import { useEnergyApi } from '../_api/EnergyApiContext';
import RecRegistryAction, { RecRegistryProcessingAction, RecRegistryActionRequest } from '../_api/data/RecRegistryAction'
import { Paginated } from '../_api/data/pagination'
import SerialNumber from '../_api/data/SerialNumber'

function serialNumberColumnValue(params: ValueGetterParams): GridCellValue {
  return params.getValue('serialNumber')?.toString
}

function quantityColumnValue(params: ValueGetterParams): GridCellValue {
  const serialNumber = new SerialNumber(params.getValue('serialNumber'))
  return serialNumber.quantity.toLocaleString()
}

function eventColumnValue(params: ValueGetterParams): GridCellValue {
  return `${params.getValue('category')} ${params.getValue('result')}`
}

function processingStatusColumn(params: GridCellParams): ReactElement {
  const status = params.value as RecRegistryProcessingAction
  switch (status) {
    case RecRegistryProcessingAction.process:
      return <CheckCircleIcon style={{ color: green[500] }} />
    case RecRegistryProcessingAction.ignore:
      return <WarningIcon style={{ color: orange[500] }} />
    case RecRegistryProcessingAction.error:
      return <ErrorIcon color="error" />
  }
}
const columns: GridColDef[] = [
  { field: 'processingAction', headerName: '', width: 150, renderCell: processingStatusColumn },
  { field: 'completedAt', headerName: 'Completed At', type: 'dateTime', width: 250 },
  { field: 'sequence', headerName: 'Seq', type: 'number', width: 100 },
  { field: 'certificateTypeId', headerName: 'Cert Type', type: 'dateTime', width: 150 },
  { field: 'registeredPersonNumber', headerName: 'RPN', width: 100 },
  { field: 'generationYear', headerName: 'Generation Year', width: 100 },
  { field: 'creationYear', headerName: 'Creation Year', hide: true, width: 100 },
  { field: 'event', headerName: 'Event', flex: 1, valueGetter: eventColumnValue },
  { field: 'ownerAccountName', headerName: 'Account', flex: 1 },
  { field: 'status', headerName: 'Status', width: 200 },
  { field: 'serialNumberColumn', headerName: 'Serials', width: 150, valueGetter: serialNumberColumnValue },
  { field: 'quantityColumn', headerName: 'Quantity', width: 100, valueGetter: quantityColumnValue },
  { field: 'processingErrorCategory', headerName: 'Error', flex: 1 },
];

type Props = {
  accreditationCodeId?: string
  generationId?: string
  serialNumber?: number
  processingError?: boolean
}

const RecRegistryActionsDataTable: React.FC<Props> = ({ accreditationCodeId, generationId, serialNumber, processingError }): React.ReactElement => {
  const defaultRequest = (): RecRegistryActionRequest => {
    const request = new RecRegistryActionRequest()
    request.accreditationCodeId = accreditationCodeId
    request.generationId = generationId
    request.processingError = processingError
    request.serialNumber = serialNumber
    return request
  }

  const energyApi = useEnergyApi();
  const [request, setRequest] = React.useState(defaultRequest())

  useEffect(() => {
    const newRequest = RecRegistryActionRequest.copy(request);
    newRequest.serialNumber = serialNumber
    setRequest(newRequest)
  }, [serialNumber]);

  const queryActions = (): Promise<Paginated<RecRegistryAction>> => {
    return energyApi.getRecRegistryActions(request)
  }

  const handlePageChange = (params: GridPageChangeParams) => {
    const newRequest = RecRegistryActionRequest.copy(request);
    newRequest.page = params.page
    newRequest.per = params.pageSize
    setRequest(newRequest);
  };

  const setSortModel = (model: GridSortModel) => {
    const newRequest = RecRegistryActionRequest.copy(request);
    newRequest.sort = model
    setRequest(newRequest);
  };

  const handleSortModelChange = (params: GridSortModelParams) => {
    if (params.sortModel !== request.sort) {
      setSortModel(params.sortModel);
    }
  };

  // Queries
  const {
    isLoading,
    isError,
    error,
    data,
    isFetching,
    isPreviousData,
  } = useQuery<Paginated<RecRegistryAction>, Error>(['recRegistryActions', request], (queryActions), { keepPreviousData: true });

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError) {
    return <span>Error...</span>
  }

  if (data) {
    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          autoHeight={true}
          rows={data.items}
          columns={columns}
          pagination
          pageSize={request.per}
          onPageSizeChange={handlePageChange}
          page={data.metadata.page}
          rowCount={data.metadata.total}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          loading={isLoading}
          disableColumnFilter
        />
      </div>
    )
  }

  return <span>Error</span>
};

RecRegistryActionsDataTable.propTypes = {
  accreditationCodeId: PropTypes.string,
  generationId: PropTypes.string,
  serialNumber: PropTypes.number,
  processingError: PropTypes.bool,
}


export interface ActionsFilterData {
  serialNumber?: number
}
interface ActionsFilterProps {
  filter: ActionsFilterData
  setFilter: React.Dispatch<React.SetStateAction<ActionsFilterData>>
}

export const ActionsFilterForm: React.FC<ActionsFilterProps> = ({ setFilter }): React.ReactElement => {
  const { register, handleSubmit } = useForm<ActionsFilterData>()

  return (
    <form noValidate onSubmit={handleSubmit(setFilter)}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        id="serialNumber"
        label="Serial Number"
        name="serialNumber"
        inputRef={register}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
      >
        Search
          </Button>
    </form>
  )
}

ActionsFilterForm.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired
}


export default RecRegistryActionsDataTable;
